/**
 * Content Encoding Utility for Justknow News Application
 * Provides secure Base64 encoding/decoding for article content fields
 * Prevents SQL injection and handles Indian language special characters
 * 
 * @author JGS Tech Team
 * @since 1.0
 */

export class ContentEncoder {
    private static readonly ENCODING_PREFIX = 'JGS_ENC_V1:';
    
    /**
     * Encodes content for secure storage using Base64 with UTF-8
     * 
     * @param content The raw content to encode
     * @returns Encoded content with JGS_ENC_V1 prefix
     * @throws Error if encoding fails
     */
    static encode(content: string): string {
        if (!content || content.trim() === '') {
            return '';
        }
        
        try {
            // Trim content and ensure proper UTF-8 encoding
            const trimmedContent = content.trim();
            
            // Use TextEncoder for proper UTF-8 handling (supports Indian languages)
            const utf8Bytes = new TextEncoder().encode(trimmedContent);
            
            // Convert to string and then Base64 encode
            const binaryString = Array.from(utf8Bytes, byte => String.fromCharCode(byte)).join('');
            const base64Content = btoa(binaryString);
            
            return this.ENCODING_PREFIX + base64Content;
            
        } catch (error) {
            console.error('Content encoding failed:', error);
            throw new Error(`Failed to encode content: ${error instanceof Error ? error.message : 'Unknown error'}`);
        }
    }
    
    /**
     * Decodes content for display purposes
     * 
     * @param encodedContent The encoded content to decode
     * @returns Decoded content, or original content if not encoded (backward compatibility)
     */
    static decode(encodedContent: string): string {
        if (!encodedContent || !encodedContent.startsWith(this.ENCODING_PREFIX)) {
            // Return as-is for backward compatibility with existing non-encoded content
            return encodedContent || '';
        }
        
        try {
            const base64Content = encodedContent.substring(this.ENCODING_PREFIX.length);
            
            if (!base64Content.trim()) {
                return '';
            }
            
            // Decode Base64 to binary string
            const binaryString = atob(base64Content);
            
            // Convert binary string to Uint8Array
            const bytes = new Uint8Array(binaryString.length);
            for (let i = 0; i < binaryString.length; i++) {
                bytes[i] = binaryString.charCodeAt(i);
            }
            
            // Use TextDecoder for proper UTF-8 decoding (supports Indian languages)
            const decodedContent = new TextDecoder('utf-8').decode(bytes);
            
            return decodedContent;
            
        } catch (error) {
            console.error('Content decoding failed:', error);
            // Return original content as fallback
            return encodedContent;
        }
    }
    
    /**
     * Validates if content is properly encoded with our format
     * 
     * @param content The content to validate
     * @returns true if content has proper JGS_ENC_V1 format
     */
    static isEncoded(content: string): boolean {
        return Boolean(content && content.startsWith(this.ENCODING_PREFIX));
    }
    
    /**
     * Validates if encoded content has proper Base64 format
     * 
     * @param encodedContent The encoded content to validate
     * @returns true if content is properly encoded, false otherwise
     */
    static isValidEncoded(encodedContent: string): boolean {
        if (!this.isEncoded(encodedContent)) {
            return false;
        }
        
        try {
            const base64Content = encodedContent.substring(this.ENCODING_PREFIX.length);
            
            if (!base64Content.trim()) {
                return false;
            }
            
            // Try to decode to validate format
            atob(base64Content);
            return true;
            
        } catch {
            return false;
        }
    }
    
    /**
     * Gets encoding statistics for monitoring purposes
     * 
     * @param originalContent The original content before encoding
     * @param encodedContent The encoded content
     * @returns Object with size and overhead information
     */
    static getEncodingStats(originalContent: string, encodedContent: string) {
        const originalSize = new Blob([originalContent || '']).size;
        const encodedSize = new Blob([encodedContent || '']).size;
        const overhead = originalSize > 0 ? ((encodedSize - originalSize) / originalSize) * 100 : 0;
        
        return {
            originalSize,
            encodedSize,
            overheadPercentage: Math.round(overhead * 100) / 100,
            compressionRatio: originalSize > 0 ? encodedSize / originalSize : 1
        };
    }
    
    /**
     * Batch encodes multiple content fields
     * 
     * @param contents Object with content fields to encode
     * @returns Object with encoded content fields
     */
    static encodeMultiple<T extends Record<string, string>>(contents: T): T {
        const encoded = {} as T;
        
        (Object.keys(contents) as Array<keyof T>).forEach(key => {
            const value = contents[key];
            (encoded as any)[key] = value ? this.encode(value) : value;
        });
        
        return encoded;
    }
    
    /**
     * Batch decodes multiple content fields
     * 
     * @param encodedContents Object with encoded content fields to decode
     * @returns Object with decoded content fields
     */
    static decodeMultiple<T extends Record<string, string>>(encodedContents: T): T {
        const decoded = {} as T;
        
        (Object.keys(encodedContents) as Array<keyof T>).forEach(key => {
            const value = encodedContents[key];
            (decoded as any)[key] = value ? this.decode(value) : value;
        });
        
        return decoded;
    }
}

/**
 * Content encoding validation utilities
 */
export class ContentEncodingValidator {
    
    /**
     * Validates content before form submission
     * 
     * @param content The content to validate
     * @param fieldName The name of the field for error reporting
     * @returns Validation result with success status and error message
     */
    static validateForSubmission(content: string, fieldName: string) {
        if (!content || content.trim() === '') {
            return {
                isValid: true,
                error: null
            };
        }
        
        if (!ContentEncoder.isEncoded(content)) {
            return {
                isValid: false,
                error: `${fieldName} must be encoded before submission`
            };
        }
        
        if (!ContentEncoder.isValidEncoded(content)) {
            return {
                isValid: false,
                error: `${fieldName} has invalid encoding format`
            };
        }
        
        return {
            isValid: true,
            error: null
        };
    }
    
    /**
     * Validates multiple content fields for submission
     * 
     * @param fields Object with field names as keys and content as values
     * @returns Validation result with list of errors
     */
    static validateMultipleFields(fields: Record<string, string>) {
        const errors: string[] = [];
        
        Object.entries(fields).forEach(([fieldName, content]) => {
            const validation = this.validateForSubmission(content, fieldName);
            if (!validation.isValid && validation.error) {
                errors.push(validation.error);
            }
        });
        
        return {
            isValid: errors.length === 0,
            errors
        };
    }
}

/**
 * Type definitions for content encoding
 */
export interface EncodedArticleContent {
    title: string;
    description: string;
    body: string;
}

export interface EncodedSubArticleContent {
    title: string;
    description?: string;
    body: string;
}

export interface EncodingStats {
    originalSize: number;
    encodedSize: number;
    overheadPercentage: number;
    compressionRatio: number;
}

export interface ValidationResult {
    isValid: boolean;
    error?: string | null;
    errors?: string[];
}